import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import moment from 'moment';
import { ContentChange, QuillEditorComponent, QuillModule } from 'ngx-quill';
import { ToastrService } from 'ngx-toastr';
import { BtnTextComponent } from '../../buttons/btn-text/btn-text.component';

import Quill from 'quill';

import ImageResizor from 'quill-image-resizor';
import GridModule from '../grid.module';
// If Quill does not come from window-object -> set find method to correct instance
ImageResizor.Quill = Quill;
GridModule.Quill = Quill;
Quill.register('modules/imageResizor', ImageResizor);
Quill.register('modules/grid', GridModule);

@Component({
  selector: 'rich-text-editor',
  standalone: true,
  imports: [QuillModule, FormsModule, ReactiveFormsModule, BtnTextComponent],
  templateUrl: './rich-text-editor.component.html',
  styleUrl: './rich-text-editor.component.css',
})
export class RichTextEditorComponent implements OnInit {
  @ViewChild('quillContainer') quillContainer!: ElementRef;
  @ViewChild('editorContent') editorContent!: QuillEditorComponent; // Reference to quill-editor
  @ViewChild('quillEditor') quillEditor!: ElementRef;
  toastr = inject(ToastrService);
  @Input() jsonFormat: string = '';
  @Input() isEditable: boolean = true;

  @Output() contentChanged = new EventEmitter<{}>();

  quillConfig = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ size: ['small', false, 'large', 'huge'] }],
      [{ color: [] }, { background: [] }],
      [{ font: [] }],
      [{ align: [] }],
      ['clean'],
      ['link', 'image'],
      // ['grid'],
      // ['s3image'],
      // ['link'],
      // ['table'],
    ],
    // grid: {},
    imageResizor: {},
    // imageResizeUpload: {
    //   awsConfig: {
    //     region: 'your-region', // e.g., 'us-east-1'
    //     credentials: {
    //       accessKeyId: 'your-access-key-id',
    //       secretAccessKey: 'your-secret-access-key',
    //     },
    //     bucketName: 'your-bucket-name',
    //   },
    // },
  };

  // icons = Quill.import('ui/icons') as Record<string, any>;

  private current = { html: '', delta: {} };

  constructor() {}

  ngOnInit() {}

  get editorModules() {
    return this.isEditable ? this.quillConfig : { toolbar: false };
  }

  onContentChanged(evt: ContentChange): void {
    this.current.html = evt.html ?? '';
    this.current.delta = evt.delta;
    this.contentChanged.emit(this.jsonFormat);
  }

  onSave() {
    var fileName = `lesson-${moment().format('DDMMYYHHmmss')}.json`;
    var a = document.createElement('a');
    var myFile = new Blob([JSON.stringify(this.jsonFormat)], {
      type: 'text/json',
    });
    a.href = URL.createObjectURL(myFile);
    a.download = fileName;
    a.click();
  }

  async onLoad(event: any) {
    const file: File = event.target.files[0];
    if (!file) return;

    try {
      const uploadFileContent = await file.text();
      const delta = JSON.parse(uploadFileContent);
      this.jsonFormat = delta;
    } catch (e) {
      this.toastr.error(
        'Failed to load or parse the file. Please ensure it is a valid AtChurch file.',
        'Error Loading File'
      );
    }
  }

  onEditorCreated(quill: Quill) {
    quill.root.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key !== 'Enter') return;

      const range = quill.getSelection();
      if (!range) return;

      const [line, offset] = quill.getLine(range.index);
      const formats = quill.getFormat(range.index);

      // If current line is already a numbered list, let Quill handle it
      if (formats['list'] === 'ordered') return;

      // Check previous line for numbered list
      const prevIndex = range.index - 1;
      if (prevIndex < 0) return;

      const prevFormats = quill.getFormat(prevIndex);
      if (prevFormats['list'] !== 'ordered') return;

      // Get the previous line blot
      const [prevLine] = quill.getLine(prevIndex);

      // Assuming prevLine is a ListItem blot
      const prevList = prevLine?.parent;
      if (!prevList || prevList.statics.blotName !== 'list') return;

      // Calculate the last number: start attr + number of items up to this one
      const start = parseInt(
        (prevList.domNode as HTMLElement).getAttribute('start') || '1',
        10
      );
      // Convert LinkedList to array to find index
      let childrenArr: any[] = [];
      let child = prevList.children.head;
      while (child) {
        childrenArr.push(child);
        child = child.next;
      }
      const itemIndex = childrenArr.indexOf(prevLine) + 1;
      let lastNumber = start + itemIndex - 1; // Adjust for 1-based

      // For nested lists, check indent level
      const currentIndent = formats['indent'] || 0;
      const prevIndent = prevFormats['indent'] || 0;
      if (currentIndent !== prevIndent) return; // Skip if indent levels differ

      // Prevent default Enter
      event.preventDefault();

      // Insert new line and format as continued ordered list
      quill.insertText(range.index, '\n', 'user');
      quill.formatText(
        range.index + 1,
        1,
        { list: 'ordered', indent: currentIndent },
        'user'
      );

      // Get the new line
      const [newLine] = quill.getLine(range.index + 1);
      const newList = newLine?.parent;

      // Set start attribute on the new <ol>
      if (newList && newList.domNode) {
        (newList.domNode as HTMLElement).setAttribute(
          'start',
          (lastNumber + 1).toString()
        );
        quill.update(); // Force update
      }

      // Set cursor to new line
      quill.setSelection(range.index + 1, 0, 'user');
    });
  }

  onPrint(): void {
    // this.ensureQuillStyles();

    // Get the HTML content from the Quill editor
    const editorContent = this.editorContent.quillEditor.root.innerHTML;

    // Wrap the editor content with necessary HTML and styles
    const html = this.wrapHtml(editorContent);

    // Create an invisible iframe for printing
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);

    // Write content to the iframe
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (doc) {
      doc.open();
      doc.write(html);
      doc.close();

      // Trigger print from the iframe
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();

      // Clean up after printing
      const handleAfterPrint = () => {
        document.body.removeChild(iframe);
        window.removeEventListener('afterprint', handleAfterPrint);
      };
      window.addEventListener('afterprint', handleAfterPrint);
    }
  }

  private wrapHtml(inner: string): string {
    return `
    <html>
      <head>
        <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/quill@2.0.3/dist/quill.core.css">
        <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/quill@2.0.3/dist/quill.snow.css">
        <style>
          @page {
            margin: 0.5in !important; /* Add 0.5-inch margin on all sides */
            size: landscape; /* Enforce landscape orientation */
          }
          body {
            margin: 0 !important;
            padding: 0 !important;
            width: 100%;
            height: 100%;
          }
          .ql-container {
            margin: 0 !important;
            padding: 20px !important; /* Add padding around the content */
            width: 100% !important;
            position: relative !important; /* Change to relative to respect page margins */
            top: 0 !important;
            left: 0 !important;
            height: auto !important;
            box-sizing: border-box !important;
          }
          .ql-editor {
            margin: 0 !important;
            padding: 0 !important; /* Keep editor padding at 0 to avoid double padding */
            width: 100% !important;
            height: auto !important;
            box-sizing: border-box !important;
          }
          img, .ql-image { /* Ensure images/diagrams scale properly */
            max-width: 100% !important;
            height: auto !important;
          }
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        </style>
      </head>
      <body>
        <div class="ql-container">
          <div class="ql-editor">${inner}</div>
        </div>
      </body>
    </html>`;
  }

  // private ensureQuillStyles(): void {
  //   const existingCore = document.querySelector('link[href*="quill.core.css"]');
  //   const existingSnow = document.querySelector('link[href*="quill.snow.css"]');
  //   if (!existingCore) {
  //     const coreCss = document.createElement('link');
  //     coreCss.rel = 'stylesheet';
  //     coreCss.href =
  //       'https://cdn.jsdelivr.net/npm/quill@1.3.7/dist/quill.core.css';
  //     document.head.appendChild(coreCss);
  //   }
  //   if (!existingSnow) {
  //     const snowCss = document.createElement('link');
  //     snowCss.rel = 'stylesheet';
  //     snowCss.href =
  //       'https://cdn.jsdelivr.net/npm/quill@1.3.7/dist/quill.snow.css';
  //     document.head.appendChild(snowCss);
  //   }
  // }
}
